/* eslint-enable complexity, no-redeclare, no-var, one-var */

import FFT from './fft'
// import { TT } from './debugger'

/**
 * @typedef {Object} SpectrogramPluginParams
 * @property {string|HTMLElement} container Selector of element or element in
 * which to render
 * @property {number} fftSamples=512 Number of samples to fetch to FFT. Must be
 * a power of 2.
 * @property {string} windowFunc='hann' The window function to be used. One of
 * these: `'bartlett'`, `'bartlettHann'`, `'blackman'`, `'cosine'`, `'gauss'`,
 * `'hamming'`, `'hann'`, `'lanczoz'`, `'rectangular'`, `'triangular'`
 * @property {?number} alpha Some window functions have this extra value.
 * (Between 0 and 1)
 * @property {number} pixelPerSecond このサイズでレンダリングする。n-overlap は自動算出される。
 * @property {?boolean} deferInit Set to true to manually call
 * `initPlugin('spectrogram')`
 * @property {?number[][]} colorMap A 256 long array of 4-element arrays.
 * Each entry should contain a float between 0 and 1 and specify
 * r, g, b, and alpha.
 * @property {?ValueRange} valueRange 値域
 */

/**
 * @typedef {Object} FFTResult
 * @property {string} params FFT パラメータのシリアライズ文字列 (@see makeFFTCacheKey)
 * @property {Array<Float32Array>} spectrums FFT 結果
 * @property {number} fftSamples FFT サンプル数
 * @property {number} peak ピーク値
 * @property {number} peakBand ピークバンド
 * @property {number} median 値の中央値
 */

/**
 * @typedef {Object} ValueRange
 * @property {number} decibelMin 値域の最小 [dB]
 * @property {number} decibelMax 値域の最大 [dB]
 * @property {number} decibelBoost ブースト境界値 [dB]
 * @property {number} ratioBoost ブースト率
 * @property {number} decibelClip クリップ境界値 [dB]
 * @property {number} ratioClip クリップ率
 */

/**
 * Render a spectrogram visualisation of the audio.
 *
 * @implements {PluginClass}
 * @extends {Observer}
 * @example
 * // es6
 * import SpectrogramPlugin from 'wavesurfer.spectrogram.js';
 *
 * // commonjs
 * var SpectrogramPlugin = require('wavesurfer.spectrogram.js');
 *
 * // if you are using <script> tags
 * var SpectrogramPlugin = window.WaveSurfer.spectrogram;
 *
 * // ... initialising wavesurfer with the plugin
 * var wavesurfer = WaveSurfer.create({
 *   // wavesurfer options ...
 *   plugins: [
 *     SpectrogramPlugin.create({
 *       // plugin options ...
 *     })
 *   ]
 * });
 */
export default class SpectrogramPlugin {
	/**
	 * Spectrogram plugin definition factory
	 *
	 * This function must be used to create a plugin definition which can be
	 * used by wavesurfer to correctly instantiate the plugin.
	 *
	 * @param  {SpectrogramPluginParams} params Parameters used to initialise the plugin
	 * @return {PluginDefinition} An object representing the plugin.
	 */
	static create (params) {
		return {
			name: 'spectrogram',
			deferInit: params && params.deferInit ? params.deferInit : false,
			params: params,
			staticProps: {
				FFT: FFT
			},
			instance: SpectrogramPlugin
		}
	}

	constructor (params, ws) {
		this.params = params
		this.wavesurfer = ws
		this.util = ws.util

		/** @type {boolean} */
		this.isFirstDrawing = true

		/** @type {ValueRange} valueRange */
		this.valueRange = params.valueRange || {
			decibelMin: -60,
			decibelMax: 60,
			decibelBoost: -30,
			ratioBoost: 3,
			decibelClip: 30,
			ratioClip: 2
		}

		/** @type {?FFTResult} FFT 結果のキャッシュ */
		this.fftCache = null

		this.frequenciesDataUrl = params.frequenciesDataUrl
		this._onScroll = e => {
			this.updateScroll(e)
		}
		this._onRender = () => {
			this.clearCache()
			this.render()
		}
		this._onWrapperClick = e => {
			this._wrapperClickHandler(e)
		}
		this._onReady = () => {
			const drawer = (this.drawer = ws.drawer)

			this.container
				= typeof params.container === 'string'
					? document.querySelector(params.container)
					: params.container

			if (!this.container) {
				throw Error('No container for WaveSurfer spectrogram')
			}
			if (params.colorMap) {
				if (params.colorMap.length < 256) {
					throw new Error('Colormap must contain 256 elements')
				}
				for (let i = 0; i < params.colorMap.length; i++) {
					const cmEntry = params.colorMap[i]
					if (cmEntry.length !== 4) {
						throw new Error(
							'ColorMap entries must contain 4 values'
						)
					}
				}
				this.colorMap = params.colorMap
			} else {
				this.colorMap = []
				for (let i = 0; i < 256; i++) {
					const val = (255 - i) / 256
					this.colorMap.push([val, val, val, 1])
				}
			}
			this.fftSamples = this.params.fftSamples || ws.params.fftSamples || 512
			this.pixelPerSecond = this.params.pixelPerSecond || 200
			this.width = 512
			this.height = this.fftSamples / 2
			this.windowFunc = params.windowFunc
			this.alpha = params.alpha

			this.createWrapper()
			this.createCanvas()
			this.render()

			drawer.wrapper.addEventListener('scroll', this._onScroll)
			ws.on('redraw', this._onRender)
		}

		this._onProgressUpdate = (done, progress) => {
			if (params.onProgressUpdate) {
				params.onProgressUpdate(done, progress)
			}
		}
	}

	init () {
		// Check if wavesurfer is ready
		if (this.wavesurfer.isReady) {
			this._onReady()
		} else {
			this.wavesurfer.once('ready', this._onReady)
		}
	}

	destroy () {
		this.unAll()
		this.wavesurfer.un('ready', this._onReady)
		this.wavesurfer.un('redraw', this._onRender)
		this.drawer && this.drawer.wrapper.removeEventListener('scroll', this._onScroll)
		this.wavesurfer = null
		this.util = null
		this.params = null
		if (this.wrapper) {
			this.wrapper.removeEventListener('click', this._onWrapperClick)
			this.wrapper.parentNode.removeChild(this.wrapper)
			this.wrapper = null
		}
	}

	createWrapper () {
		const prevSpectrogram = this.container.querySelector('spectrogram')
		if (prevSpectrogram) {
			this.container.removeChild(prevSpectrogram)
		}
		const wsParams = this.wavesurfer.params
		this.wrapper = document.createElement('spectrogram')

		this.drawer.style(this.wrapper, {
			display: 'block',
			position: 'relative',
			userSelect: 'none',
			webkitUserSelect: 'none',
			height: `${this.height}px`
		})

		if (wsParams.fillParent || wsParams.scrollParent) {
			this.drawer.style(this.wrapper, {
				width: '100%',
				overflowX: 'hidden',
				overflowY: 'hidden'
			})
		}
		this.container.appendChild(this.wrapper)

		this.wrapper.addEventListener('click', this._onWrapperClick)
	}

	_wrapperClickHandler (event) {
		event.preventDefault()
		const relX = 'offsetX' in event ? event.offsetX : event.layerX
		this.fireEvent('click', relX / this.width || 0)
	}

	createCanvas () {
		const canvas = (this.canvas = this.wrapper.appendChild(
			document.createElement('canvas')
		))

		/** @type {CanvasRenderingContext2D} */
		this.spectrCc = canvas.getContext('2d')

		this.util.style(canvas, {
			position: 'absolute',
			zIndex: 4
		})
	}

	clearCache () {
		this.fftCache = null
	}

	render () {
		this._onProgressUpdate(false, 0)

		if (this.isFirstDrawing) {
			this.updateCanvasStyle()
			this.isFirstDrawing = false
		}
		this.clearCanvas()

		setTimeout(() => {
			if (this.frequenciesDataUrl) {
				this.loadFrequenciesData(this.frequenciesDataUrl)
			} else {
				this.getFrequencies(this.drawSpectrogram)
			}
		}, 10)
	}

	clearCanvas () {
		const { canvas, spectrCc } = this
		if (!canvas || !spectrCc) return

		const color = 'rgba('
			+ Math.round(this.colorMap[0][0] * 255) + ', '
			+ Math.round(this.colorMap[0][1] * 255) + ', '
			+ Math.round(this.colorMap[0][2] * 255) + ', '
			+ Math.round(this.colorMap[0][3] * 255) + ')'

		spectrCc.fillStyle = color
		spectrCc.fillRect(0, 0, canvas.width, canvas.height)
	}

	renderWithCache () {
		if (this.cachedResampledData) {
			this._onProgressUpdate(false, 95)
			this.drawSpectrogram(null)
		} else {
			this.render()
		}
	}

	updateCanvasStyle () {
		this.canvas.width = this.width
		this.canvas.height = this.height
		this.canvas.style.width = '100%'
		this.canvas.style.height = `${this.height}px`
	}

	drawSpectrogram (frequenciesData) {
		const spectrCc = this.spectrCc
		if (!spectrCc) return

		this.updateCanvasStyle()

		const height = this.height
		const width = this.width
		const pixels = frequenciesData
		const heightFactor = this.buffer ? 2 / this.buffer.numberOfChannels : 1

		const divWidth = 1000
		const divs = Math.ceil(width / divWidth)

		let div
		let i
		let j
		let k

		for (div = 0; div < divs; div++) {
			const imageData = spectrCc.createImageData(divWidth, height)
			const x0 = divWidth * div
			const iMax = Math.min(x0 + divWidth, pixels.length)

			for (i = x0; i < iMax; i++) {
				if (i < x0 || i >= x0 + divWidth) continue
				const x = i - x0

				for (j = 0; j < pixels[i].length; j++) {
					const colorMap = this.colorMap[pixels[i][j]]
					/* eslint-disable max-depth */
					for (k = 0; k < heightFactor; k++) {
						let y = height - j * heightFactor
						if (heightFactor === 2 && k === 1) {
							y--
						}
						const redIndex = y * (divWidth * 4) + x * 4
						imageData.data[redIndex] = colorMap[0] * 255
						imageData.data[redIndex + 1] = colorMap[1] * 255
						imageData.data[redIndex + 2] = colorMap[2] * 255
						imageData.data[redIndex + 3] = colorMap[3] * 255
					}
					/* eslint-enable max-depth */
				}
			}
			spectrCc.putImageData(imageData, divWidth * div, 0)

			this._onProgressUpdate(false, 95 + 5 * (div + 1) / divs)
		}

		// console.log('Time to draw spectrogram:', TT())
		this._onProgressUpdate(true, 100)
	}

	getFrequencies (callback) {
		const buffer = (this.buffer = this.wavesurfer.backend.buffer)
		const sampleRate = buffer.sampleRate
		const fftSamples = this.fftSamples
		const noverlap = Math.floor(fftSamples - (sampleRate / this.pixelPerSecond))
		this.width = Math.floor((buffer.length - fftSamples) / (fftSamples - noverlap))

		this.getFrequenciesWithParams({
			callback,
			buffer,
			begin: 0,
			end: buffer.length,
			fftSamples,
			noverlap
		}, this.valueRange)
	}

	/**
	 *
	 * @param {Object} param0
	 * @param {ValueRange} vr
	 */
	getFrequenciesWithParams ({ callback, buffer, begin, end, fftSamples, noverlap }, vr) {
		// TT()

		const params = this.makeFFTCacheKey({ begin, end, fftSamples, noverlap })
		if (!this.fftCache || this.fftCache.params !== params) {
			console.log('No FFT cache')
			this.getFFTResult({ buffer, begin, end, fftSamples, noverlap })
		}
		const { spectrums, median } = this.fftCache

		// Boost/clip 後の dB 値の範囲
		const adjustedMin = (vr.decibelMin < vr.decibelBoost)
			? ((vr.decibelMin - vr.decibelBoost) / vr.ratioBoost + vr.decibelBoost)
			: vr.decibelMin
		const adjustedMax = (vr.decibelMax > vr.decibelClip)
			? ((vr.decibelMax - vr.decibelClip) / vr.ratioClip + vr.decibelClip)
			: vr.decibelMax

		// 中央値を 0 dB とするためのオフセット (log(ab) = log(a) + log(b))
		const decibelOffset = -Math.log10(median)

		// 256 段階にするための係数
		const amplitude = 255 / (adjustedMax - adjustedMin)

		// Boost/clip を適用した dB 値に変換する
		const toAdjustedDecibel = (v) => {
			const decibel = 10 * (Math.log10(v) + decibelOffset)
			if (decibel < vr.decibelBoost) {
				return (decibel - vr.decibelBoost) / vr.ratioBoost + vr.decibelBoost
			} else if (decibel > vr.decibelClip) {
				return (decibel - vr.decibelClip) / vr.ratioClip + vr.decibelClip
			} else {
				return decibel
			}
		}

		try {
			const length = spectrums.length
			const frequencies = spectrums.map((spectrum, index) => {
				const array = new Uint8Array(fftSamples / 2)
				for (let j = 0; j < fftSamples / 2; j++) {
					array[j] = Math.max(0, Math.min(255, amplitude * (toAdjustedDecibel(spectrum[j]) - adjustedMin)))
				}

				this._onProgressUpdate(false, 45 + 40 * index / length)

				return array
			})

			// console.log('Time to map frequencies:', TT())

			// console.log(`frequencies.length = ${frequencies.length} vs this.width = ${this.width}`)
			callback.call(this, frequencies)
		} catch (e) {
			console.error(e)
		}
	}

	/**
	 * 浮動小数点数で FFT 演算結果を得る
	 * @param {*} param0
	 * @returns {Array<Float32Array>}
	 */
	getFFTResult ({ buffer, begin, end, fftSamples, noverlap }) {
		if (!buffer) {
			this.fireEvent('error', 'Web Audio buffer is not available')
			return
		}

		const channelOne = buffer.getChannelData(0)
		const sampleRate = buffer.sampleRate

		// console.log('buffer.length=', buffer.length, ', channelOne.length = ', channelOne.length)
		// console.log('FFT samples =', fftSamples)
		// console.log(`n-overlap = ${noverlap}`)

		const fft = new FFT(
			fftSamples,
			sampleRate,
			this.windowFunc,
			this.alpha
		)
		let currentOffset = begin

		const spectrums = []
		const offsetIncr = fftSamples - noverlap
		while (currentOffset + fftSamples < end) {
			const segment = channelOne.slice(
				currentOffset,
				currentOffset + fftSamples
			)
			spectrums.push(fft.calculateSpectrum(segment))
			currentOffset += offsetIncr

			const progress = (currentOffset - begin) / (end - begin)
			this._onProgressUpdate(false, 40 * progress)
		}

		// console.log('Time to FFT:', TT())
		// console.log('peak value =', fft.peak, ', peak band =', fft.peakBand)

		const result = {
			params: this.makeFFTCacheKey({ begin, end, fftSamples, noverlap }),
			spectrums: spectrums,
			fftSamples,
			peak: fft.peak,
			peakBand: fft.peakBand,
			median: 0
		}

		result.median = this.calcMedian(result)
		// console.log('Time to calc median:', TT())
		this._onProgressUpdate(false, 45)

		this.fftCache = result

		return result
	}

	/**
	 * FFT 演算結果の中央値を計算する
	 * @param {FFTResult} result
	 */
	calcMedian (result) {
		/**
		 * 120 dB のレンジを 255 分割したバケツの閾値。
		 * 添字 0 が −120 dB, 添字 255 が 0 dB の閾値。
		 * @var {Float32Array} thresholds
		 */
		const thresholds = new Float32Array(256)
		for (let t = 0; t < 255; t++) {
			thresholds[t] = result.peak * Math.pow(10, 12 / 255 * t - 12)
		}
		thresholds[255] = Infinity

		/**
		 * FFT 値のヒストグラム。
		 * メモリ消費を抑えつつ中央値を求めるために使用する。
		 * コンストラクタで要素が 0 で初期化されることに留意
		 * @var {Uint32Array} hist
		 */
		const hist = new Uint32Array(256)
		const skip = 29
		let valueCount = 0
		for (let i = 0, li = result.spectrums.length; i < li; i += skip) {
			result.spectrums[i].forEach(value => {
				for (let t = 0; t < 256; t++) {
					if (value <= thresholds[t]) {
						hist[t] += 1
						valueCount += 1
						break
					}
				}
			})
		}

		// 中央値を求める
		let counted = 0
		const center = valueCount / 2
		const medianIndex = hist.findIndex((n) => {
			counted += n
			return (counted >= center)
		})
		const median = (thresholds[Math.max(0, medianIndex - 1)] + thresholds[medianIndex]) / 2
		// console.log('中央値 =', median)
		return median
	}

	makeFFTCacheKey ({ begin, end, fftSamples, noverlap }) {
		return `range=[${begin},${end}],fftSamples=${fftSamples},noverlap=${noverlap}`
	}

	loadFrequenciesData (url) {
		const request = this.util.fetchFile({ url: url })

		request.on('success', data =>
			this.drawSpectrogram(JSON.parse(data))
		)
		request.on('error', e => this.fireEvent('error', e))

		return request
	}

	freqType (freq) {
		return freq >= 1000 ? (freq / 1000).toFixed(1) : Math.round(freq)
	}

	unitType (freq) {
		return freq >= 1000 ? 'KHz' : 'Hz'
	}

	updateScroll (e) {
		if (this.wrapper) {
			this.wrapper.scrollLeft = e.target.scrollLeft
		}
	}
}
